import {useEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import ModalWindow from 'components/molecules/ModalWindow'
import {Button, StackIcon} from 'shared/ui'
import type {Webhook, TestWebhookResultVM} from '../../types'
import {webhookEventToDisplayName} from '../../types'
import {api} from '../../api'
import {handleWebhookTestResponse} from '../../utils/webhookNotifications'
import css from './WebhookList.module.scss'
import {notifyError} from 'shared/errors/notificate'

const TEST_DROPDOWN_ITEM_IDS = [
	'contact.connected',
	'contact.scrapped',
	'contact.notes.changed',
	'contact.qualification.changed',
	'invitation.sent',
	'message.sent',
	'message.received'
] as const

const TEST_DROPDOWN_ITEMS = TEST_DROPDOWN_ITEM_IDS.map(id => ({
	id,
	title: webhookEventToDisplayName(id)
}))

interface WebhookListProps {
	webhooks: Webhook[]
	onEdit: (id: string) => void
	canManage?: boolean
}

export function WebhookList({webhooks, onEdit, canManage = true}: WebhookListProps) {
	const [webhookToDelete, setWebhookToDelete] = useState<Webhook | null>(null)
	const [openTestId, setOpenTestId] = useState<string | null>(null)
	const testWrapRef = useRef<HTMLDivElement | null>(null)
	const [deleteWebhook, deleteOptions] = api.useDeleteWebhookMutation()
	const [testContactConnected] = api.useTestContactConnectedMutation()
	const [testInvitationSent] = api.useTestInvitationSentMutation()
  const [testContactScrapped] = api.useTestContactScrappedMutation()
  const [testContactNotesChanged] = api.useTestContactNotesChangedMutation()
  const [testContactQualificationChanged] = api.useTestContactQualificationChangedMutation()
  const [testMessageSent] = api.useTestMessageSentMutation()
  const [testMessageReceived] = api.useTestMessageReceivedMutation()

	useEffect(() => {
		if (!openTestId) {
			return;
		}

		const close = (e: MouseEvent) => {
			if (testWrapRef.current?.contains(e.target as Node)) {
				return;
			}

			setOpenTestId(null)
		}
		document.addEventListener('click', close)
		return () => document.removeEventListener('click', close)
	}, [openTestId])

	const handleRequestDelete = (webhook: Webhook) => {
		if (!canManage) {
			return;
		}

		setWebhookToDelete(webhook)
	}

	const handleConfirmDelete = async () => {
		if (!canManage || !webhookToDelete) {
			return;
		}

		try {
			await deleteWebhook(webhookToDelete.id).unwrap()
		} finally {
			setWebhookToDelete(null)
		}
	}

	const handleTestAction = async (webhookId: string, itemId: string) => {
		setOpenTestId(null)

		try {
			let result: TestWebhookResultVM
			switch (itemId) {
				case 'contact.connected':
					result = await testContactConnected(webhookId).unwrap()
					break
				case 'invitation.sent':
					result = await testInvitationSent(webhookId).unwrap()
					break
        case 'contact.scrapped':
          result = await testContactScrapped(webhookId).unwrap()
          break
        case 'contact.notes.changed':
          result = await testContactNotesChanged(webhookId).unwrap()
          break
        case 'contact.qualification.changed':
          result = await testContactQualificationChanged(webhookId).unwrap()
          break
        case 'message.sent':
          result = await testMessageSent(webhookId).unwrap()
          break
        case 'message.received':
          result = await testMessageReceived(webhookId).unwrap()
          break
        default:
					result = {
						success: false,
						message: `Unknown test event type: ${itemId}`,
						status_code: 400
					}
					break
			}

			handleWebhookTestResponse(result)
		} catch (error) {
			notifyError(error)
		}
	}

	if (webhooks.length === 0) {
		if (!canManage) {
			return null
		}

		return (
			<div className={css.empty}>
				<p>No webhooks configured.</p>
			</div>
		)
	}

	return (
		<div className={css.list}>
			{webhooks.map(webhook => {
				const isTestOpen = openTestId === webhook.id
				return (
					<div
						key={webhook.id}
						className={clsx(css.webhookCard, {[css.disabled]: !canManage})}
					>
						<div className={css.webhookContent}>
							<div className={css.url}>{webhook.uri}</div>
							{webhook.events.length > 0 ? (
								<div className={css.events}>
									{webhook.events.map((event, index) => (
										<span key={index} className={css.eventTag}>
											{webhookEventToDisplayName(event)}
										</span>
									))}
								</div>
							) : (
								<div className={css.noTriggers}>
									No triggers selected
								</div>
							)}
						</div>
						<div className={css.actions}>
							<div
								className={css.testWrap}
								ref={isTestOpen ? testWrapRef : undefined}
							>
								<Button
									className={css.actionBtn}
									disabled={!canManage}
									onClick={() => setOpenTestId(isTestOpen ? null : webhook.id)}
									variant='secondary'
								>
									Test
									<StackIcon className={css.caret} icon='arrow' width={12}/>
								</Button>
								{isTestOpen && (
									<div className={css.testDropdown}>
										{TEST_DROPDOWN_ITEMS.map(item => (
											<button
												className={css.testDropdownItem}
												key={item.id}
												onClick={() => handleTestAction(webhook.id, item.id)}
												type='button'
											>
												{item.title}
											</button>
										))}
									</div>
								)}
							</div>

							<Button
								className={css.actionBtn}
								disabled={!canManage}
								onClick={() => onEdit(webhook.id)}
								variant='bordered'
							>
								Edit
							</Button>
							<Button
								className={`${css.actionBtn} ${css.deleteButton}`}
								disabled={!canManage || deleteOptions.isLoading}
								onClick={() => handleRequestDelete(webhook)}
								variant='bordered'
							>
								Delete
							</Button>
						</div>
					</div>
				)
			})}

			<ModalWindow
				closeHandler={() => setWebhookToDelete(null)}
				closeText='No'
				confirmHandler={handleConfirmDelete}
				confirmText='Yes'
				content={
					webhookToDelete
						? `Webhook "${webhookToDelete.uri}" will be deleted.`
						: ''
				}
				loading={deleteOptions.isLoading}
				open={Boolean(webhookToDelete)}
				title='Delete webhook?'
			/>
		</div>
	)
}